import { ApiClientService, EntityApiService, RequestConfig, SearchResponseType } from '@dfl/react-security';
import { TermFilter } from '@dofleini/query-builder';
import { ICategory } from 'modules/inventory/settings/category/interfaces';

class CategoryService extends EntityApiService<ICategory> {
  private readonly sortCategoriesHierarchically = (categories: ICategory[]): ICategory[] => {
    const result: ICategory[] = [];
    const categoryMap = new Map<string, ICategory>();

    categories.forEach((cat: ICategory) => {
      if (!cat?.parent) {
        cat.parent = cat?._id;
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      categoryMap.set(cat._id!, cat);
    });

    const addCategoryAndChildren = (parentId: string | null) => {
      const children = categories
        .filter((cat) => {
          if (parentId === null) {
            return cat._id === cat.parent;
          }
          return cat.parent === parentId && cat._id !== cat.parent;
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      children.forEach((child) => {
        result.push(child);

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        addCategoryAndChildren(child._id!);
      });
    };

    addCategoryAndChildren(null);

    return result;
  };

  searchClean = (params?: any, config?: RequestConfig): Promise<SearchResponseType<ICategory>> => {
    const searchParams = {
      ...params,
      projections: {
        description: 0,
        visible: 0,
        image: 0,
        createdAt: 0,
        owner: 0,
        updatedAt: 0,
      },
      sort: {
        order: 1,
      },
    };

    return this.search(searchParams, config).then((data) => {
      data.data = this.sortCategoriesHierarchically(data.data);
      return data;
    });
  };

  searchAll = (): Promise<ICategory[]> => {
    return this.handleResponse(ApiClientService.post(this.getPath('/search'), { size: 200 }));
  };

  searchByParent = (parentId: string | null): Promise<SearchResponseType<ICategory>> => {
    const filters =
      parentId === null
        ? new TermFilter({ field: 'parent', value: null })
        : new TermFilter({ field: 'parent', value: parentId, objectId: true });

    return this.search({
      size: 200,
      filters,
      projections: { description: 0, visible: 0, image: 0, createdAt: 0, owner: 0, updatedAt: 0 },
      sort: { order: 1 },
    });
  };

  updateMany = (payload: any) => {
    return this.handleResponse(ApiClientService.post(this.getPath('/updateMany'), payload));
  };
}

export default new CategoryService('/ms-inventory/api/categories');
